import { createReducer } from "reduxsauce"
import { call, put, takeEvery, delay, takeLatest, getContext } from "redux-saga/effects"
import { post, get as getApi } from "api"
import { get, startsWith } from "lodash"
import actionCreator from "helpers/actionCreator"
import { receiveUser, sessionExpired } from "../../../actions/user"
import { getRedirectUrl } from "../../helpers/routes"
import errorCodes from "../../helpers/errors"
import { NotFoundError, TooManyRequestsError, ValidationError } from "api/errors"
import { secondsToTimePhrase } from "helpers/dates"
import { isCaptchaRequired } from "./helpers"

export const { Types, Creators } = actionCreator("ompnt/modules/login", {
  setBasicAuth: {
    props: ["email", "passwordExpired"],
    track: true,
    trackType: "login-password",
    trackPayload: ({ email }) => ({ email }),
  },
  trackSsoLogin: {
    track: true,
    trackType: "login-sso",
  },
  trackLoginSuccess: {
    track: true,
    trackType: "login-success",
  },
  fetchAuthType: ["email", "redirect"],
  login: {
    props: ["email", "password", "mfaToken", "redirect", "captchaToken", "serverRedirect"],
    track: true,
    trackType: "login-password-button",
    trackPayload: ({ email, redirect, serverRedirect }) => ({ email, redirect: redirect || serverRedirect }),
  },
  refresh: {
    props: ["email", "password", "token", "captchaToken"],
    track: true,
    trackType: "refresh-login-password-button",
    trackPayload: ({ email }) => ({ email }),
  },
  refreshSso: {
    props: ["email"],
    track: true,
    trackType: "refresh-login-sso-button",
  },
  listenSsoLogin: {
    props: [],
    track: true,
    trackType: "sign-in-sso-refresh-completed",
  },
  setErrorMessage: {
    props: ["error"],
    track: true,
    trackType: "login-error-notification-sent",
  },
  reset: [],
  clearErrorMessage: [],
  setRequireMfa: ["value"],
  setRequireCaptcha: ["value"],
})

const INITIAL_STATE = {
  isBasicAuth: false,
  selectedEmail: null,
  errorMessage: null,
  requireMfa: false,
  requireCaptcha: false,
  passwordExpired: false,
}

let win

function handleSetBasicAuth(state, { email, passwordExpired }) {
  return { ...state, isBasicAuth: true, selectedEmail: email, passwordExpired }
}

function handleReset() {
  return { ...INITIAL_STATE }
}

function handleClearError(state) {
  return { ...state, errorMessage: null }
}

function handleSetErrorMessage(state, { error }) {
  return { ...state, errorMessage: error }
}

function handleSetRequireMfa(state, { value }) {
  return { ...state, requireMfa: value }
}

function handleSetRequireCaptcha(state, { value }) {
  return { ...state, requireCaptcha: value }
}

export const HANDLERS = {
  [Types.SET_BASIC_AUTH]: handleSetBasicAuth,
  [Types.RESET]: handleReset,
  [Types.SET_ERROR_MESSAGE]: handleSetErrorMessage,
  [Types.CLEAR_ERROR_MESSAGE]: handleClearError,
  [Types.SET_REQUIRE_MFA]: handleSetRequireMfa,
  [Types.SET_REQUIRE_CAPTCHA]: handleSetRequireCaptcha,
}

export default createReducer(INITIAL_STATE, HANDLERS)

function* fetchAuthType({ email, redirect }) {
  try {
    // clear the existing tokens since it's a new login
    for (let i = localStorage.length - 1; i >= 0; i--) {
      if (startsWith(localStorage.key(i), "ompnt-")) {
        localStorage.removeItem(localStorage.key(i))
      }
    }

    const { basicAuth, passwordExpired, url, captchaRequired } = yield call(post, "/auth", { email, redirect }, true)
    yield put(Creators.setRequireCaptcha(captchaRequired))
    if (basicAuth) {
      yield put(Creators.setBasicAuth(email, passwordExpired))
    } else {
      yield put(Creators.trackSsoLogin(email))
      window.location.href = url
    }
  } catch (err) {
    let error = "An unexpected error occured."

    if (err instanceof TooManyRequestsError) {
      const timePhrase = secondsToTimePhrase(err.retryAfter)
      error = `You've reached the maximum number of expired password reset attempts. Try again ${timePhrase}.`
    }

    yield put(Creators.setErrorMessage(error))
  }
}

function* login({ email, password, mfaToken, redirect, captchaToken, serverRedirect }) {
  try {
    const { url, mfaEnabled } = yield call(post, "/sessions", { email, password, redirect, mfaToken, captchaToken }, true)

    if (mfaEnabled) {
      yield put(Creators.setRequireMfa(true))
    } else {
      // This call is using the session cookie to identify the user
      const { user } = yield call(getApi, "/api/users/me", true)
      yield put(receiveUser({ ...user }))
      yield put(Creators.trackLoginSuccess())

      yield put(Creators.setRequireMfa(false))

      if (serverRedirect) {
        // Redirecting with window.location so that pi-server gets called again to match the route
        window.location = serverRedirect
      } else {
        const redirectUrl = getRedirectUrl(user, url)
        if (redirectUrl) {
          const { navigate } = yield getContext("router")
          yield call(navigate, redirectUrl)
        }
      }
    }
  } catch (err) {
    if (err instanceof ValidationError || err instanceof NotFoundError) {
      yield put(Creators.setRequireCaptcha(isCaptchaRequired(err)))
    }
    yield put(Creators.setErrorMessage(err.errors || err.response))
  }
}

function* refresh({ email, password, token, captchaToken }) {
  try {
    yield call(post, "/sessions", { email, password, mfaToken: token, captchaToken }, true)
    yield put(sessionExpired({ sessionExpired: false }))
  } catch (err) {
    if (err instanceof ValidationError || err instanceof NotFoundError) {
      yield put(Creators.setRequireCaptcha(isCaptchaRequired(err)))
    }
    yield put(Creators.setErrorMessage(err.errors || err.response))
  }
  yield put(Creators.setRequireMfa(false))
}

function* refreshSso({ email }) {
  try {
    if (win) {
      win.close()
    }

    const { url } = yield call(post, "/auth", { email, refreshSession: true }, true)
    yield call([localStorage, localStorage.removeItem], "ompnt-refresh")
    yield call(window.open, url, "", "menubar=no,toolbar=no, height=600, width=450")
    yield put(Creators.listenSsoLogin())
  } catch (err) {
    yield put(Creators.setErrorMessage(err.response))
  }
}

function* handleListenSsoLogin() {
  let waitingSignIn = true
  let errorCode
  while (waitingSignIn) {
    errorCode = yield call([localStorage, localStorage.getItem], "ompnt-refresh")
    if (errorCode === null) {
      yield delay(100)
    } else {
      waitingSignIn = false
    }
  }

  yield call([localStorage, localStorage.removeItem], "ompnt-refresh")

  if (errorCode) {
    const errorMessage = get(errorCodes, errorCode)
    yield put(Creators.setErrorMessage(errorMessage))
  } else {
    yield put(sessionExpired({ sessionExpired: false }))
  }
}

export function* saga() {
  yield takeEvery(Types.FETCH_AUTH_TYPE, fetchAuthType)
  yield takeEvery(Types.LOGIN, login)
  yield takeEvery(Types.REFRESH, refresh)
  yield takeEvery(Types.REFRESH_SSO, refreshSso)
  yield takeLatest(Types.LISTEN_SSO_LOGIN, handleListenSsoLogin)
}
